// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-127 — Sección "Roles y Permisos" del Admin.
//
// Tabla de administradores de Brillamos (búsqueda por email) + botón "Agregar administrador".
// La edición de rol/alcance/permisos (AdminRoleEditorModal) vive en un modal, compartido entre
// alta y edición — ambos terminan en la misma mutación (PUT v1/users/{id}/recognition-role).
// Las filas AdminGlobal no exponen acción de editar: ese rol se gestiona desde la Administración
// General, fuera de esta pantalla. Consume UserAdminService: useAdmins (GET). Estados
// loading/error/empty reales. CERO mocks.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAdmins, useUpdateUserRoles, ALL_FALSE, getRecognitionErrorMessage, getRecognitionErrorStatus,
} from '@/api/recognition';
import type { AdminUserDto } from '@/api/recognition';
import { ConfirmModal, InitialsAvatar, Toast, useToast } from '@/components/common';
import { useOUItems } from '@/hooks/useOUs';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import { canManageUser } from '@/auth/roleGrants';
import { StateBlock } from '../components';
import { AdminSection, AdminPagerFooter, usePagination } from './adminShared';
import { AdminRoleEditorModal, type AdminEditorTarget } from './AdminRoleEditorModal';

type EditorState = { mode: 'closed' } | AdminEditorTarget;

export function AdminRoles() {
  const { t } = useTranslation('recognition');
  const admins = useAdmins();
  // BUG (QA, 2026-08-23) — Editar/Quitar se mostraban para CUALQUIER fila que no fuera AdminGlobal,
  // sin importar si quien mira la pantalla tiene autoridad real sobre esa persona. El backend sí lo
  // exige (RoleGrantPolicy.CanManageUser, ver cabecera de AdminRoleEditorModal.tsx) y devuelve 403 al
  // guardar, pero el botón seguía ahí invitando a intentarlo — entre dos Admin OU Reconocimiento
  // (mismo rol, ambos sin nada que delegar) el 403 es el comportamiento CORRECTO; lo que estaba mal
  // era ofrecer el botón. `canManageUser` es el mismo espejo de esa política que ya usa
  // `GestionAdministradores.tsx` (Pulso) para lo mismo.
  const { activeRoles, recognitionOuScope: actorOuScope } = useVisibleNavLinks();
  // 2026-08-23 — un Admin OU Reconocimiento sin rol tenant-wide (ni AdminGlobal ni AdminBrillamos)
  // administra Empleados/Admin OU Reconocimiento SOLO dentro de su propia OU delegada
  // (RoleGrantPolicy.GrantableBy + RecognitionScopeGuard.CanManageRecognitionAdmin, backend). Si el
  // actor combina este rol con uno tenant-wide, esa rama ya no tiene restricción de OU que aplicar.
  const isOuScopedOnlyActor = activeRoles.includes('AdminOUBrillamos')
    && !activeRoles.includes('AdminGlobal') && !activeRoles.includes('AdminBrillamos');
  // `recognitionOuScope` es opcional en `VisibleNavLinks` (mocks de pantallas ajenas a Brillamos no
  // lo declaran); sin restricción por default, igual que el resto de los campos de esa interfaz.
  const recognitionOuScope = actorOuScope ?? { rootOuIds: [], allOusAccess: true };
  // Bug (permisos/vistas, 2026-08-18) — la columna «Alcance» imprimía `scopeOuNames` tal cual: el
  // backend cascada cada raíz configurada a TODOS sus descendientes reales antes de persistir
  // (RN-R07), así que para una OU como MEXICO —con ~1400 de los ~1986 nodos del catálogo real—
  // esto era literalmente el árbol entero volcado en una celda. La columna debe mostrar solo el
  // nivel más alto que se configuró (la raíz elegida), no la cascada completa que resultó de ella.
  const { data: allOuItems = [] } = useOUItems({ activeOnly: false, rootsOnly: false });
  const ouParentById = useMemo(
    () => new Map(allOuItems.map((item) => [item.id, item.parentOuId ?? null])),
    [allOuItems],
  );
  // Regresión (2026-08-12, commit 2655278af) — `useOUItems()` cambió su default a `rootsOnly: true`
  // (§ ruido del catálogo AD, ver useOUs.ts) y esta pantalla nunca se auditó: el árbol del modal de
  // alta/edición pasó de ser granular (cualquier departamento real, con expand/collapse) a solo 6
  // checkboxes planos de país, sin darse cuenta. Efecto en cadena: al no poder elegir nada más
  // angosto que un país entero, CADA alcance delegado cascadea (RN-R07) sobre casi todo ese país —
  // el mismo volcado de ~1400 nombres que motivó el fix de la columna de arriba. Se restaura el
  // árbol completo para el picker; el catálogo de 1986 nodos ya soporta la jerarquía real.
  //
  // 🔴 NO filtrar por `isActive` aquí: `buildOuTree` (OuTree/ouTree.ts) resuelve el padre de cada
  // nodo buscándolo en ESTA MISMA lista — si su padre real está inactivo y se filtra, el hijo
  // (activo) se queda sin padre conocido y `buildOuTree` lo trata como huérfano, es decir, como
  // RAÍZ. Con una jerarquía real de 4 niveles eso no es un caso raro: produce decenas de nodos
  // repetidos (mismo nombre, distinto id) todos aplanados al primer nivel — exactamente el árbol
  // roto que se vio en pantalla al probar este fix. El árbol necesita la cadena de padres COMPLETA
  // para estructurarse bien; filtrar por actividad es un problema de qué mostrar seleccionable,
  // no de qué usar para construir la forma del árbol.
  const ouItems = allOuItems;

  // 2026-08-23 — "Alcance" solo debe ofrecer las OUs que el actor administra: mostrarle el catálogo
  // entero (miles de nodos fuera de su alcance real) confunde y el backend lo va a rechazar igual.
  // Sin restricción para actores tenant-wide (AdminGlobal/AdminBrillamos) o con "todas las OUs".
  const scopedOuItems = useMemo(() => {
    if (!isOuScopedOnlyActor || recognitionOuScope.allOusAccess) return ouItems;
    const mine = new Set(recognitionOuScope.rootOuIds);
    return ouItems.filter((item) => mine.has(item.id));
  }, [ouItems, isOuScopedOnlyActor, recognitionOuScope]);

  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });
  // Bug (permisos, 2026-08-18) — no existía forma de quitar el rol de Brillamos de un admin desde
  // esta pantalla: solo "Editar" (que abre el modal), y dentro del modal el único interruptor
  // ("Estado") no sirve para esto — apaga `IsActive`, el MISMO flag que usa el login: lo dejaría
  // sin acceso a TODA la app, no solo a Brillamos (ver `FrontlineLoginCommandHandler`). "Quitar"
  // sigue el patrón real de `GestionAdministradores.tsx` (Pulso): degrada a `Employee` y revoca
  // los permisos, sin tocar `active` en absoluto — conserva la cuenta y el resto de sus accesos.
  const [removeTarget, setRemoveTarget] = useState<AdminUserDto | null>(null);
  const removeAdmin = useUpdateUserRoles();
  const { toast, showError } = useToast();

  function confirmRemove() {
    if (!removeTarget) return;
    // Sin `active`: quitar el rol de administrador no toca la cuenta (2026-10-21). Antes se
    // reenviaba el `active` de la lista en caché y, si estaba viejo, la cuenta quedaba dada de baja.
    const { id } = removeTarget;
    removeAdmin.mutate(
      { id, body: { role: 'Employee', scopeOuIds: [], permissions: ALL_FALSE } },
      {
        onError: (e) => showError(
          getRecognitionErrorStatus(e) === 403
            ? t('admin.roles.saveForbidden')
            : getRecognitionErrorMessage(e, t('admin.roles.removeError')),
        ),
      },
    );
    setRemoveTarget(null);
  }

  const allAdmins = useMemo(() => admins.data ?? [], [admins.data]);
  // 2026-08-23 — cierre de fuga: oculta (no solo deshabilita "Editar") a un Admin OU Reconocimiento
  // cuyo propio alcance delegado se sale del alcance del actor, aunque comparta OU de pertenencia y
  // rol — verlo listado sin poder tocarlo es justo la confusión que se quiere evitar. El backend
  // aplica la misma regla (RecognitionScopeGuard.CanManageRecognitionAdmin) como defensa en
  // profundidad; este filtro es solo de presentación.
  const list = useMemo(() => {
    if (!isOuScopedOnlyActor || recognitionOuScope.allOusAccess) return allAdmins;
    const mine = new Set(recognitionOuScope.rootOuIds);
    return allAdmins.filter((admin) => admin.role !== 'AdminOUBrillamos' || admin.scopeOuIds.every((id) => mine.has(id)));
  }, [allAdmins, isOuScopedOnlyActor, recognitionOuScope]);
  const filtered = useMemo(
    () => list.filter((a) => (a.email + (a.displayName ?? '')).toLowerCase().includes(query.trim().toLowerCase())),
    [list, query],
  );
  const pg = usePagination(filtered, 10);

  /**
   * De la cascada completa guardada en `scopeOuIds`, se queda solo con los ids cuyo padre NO está
   * también en el propio alcance: esos son las raíces que alguien eligió de verdad (o, para una OU
   * de catálogo, la raíz global). Todo lo demás es descendencia que la cascada trajo consigo.
   */
  const scopeDisplayByAdminId = useMemo(() => {
    const map = new Map<string, string>();
    for (const admin of list) {
      const idSet = new Set(admin.scopeOuIds);
      const topLevelNames = admin.scopeOuIds
        .map((id, index) => ({ id, name: admin.scopeOuNames[index] ?? id }))
        .filter(({ id }) => {
          const parentId = ouParentById.get(id);
          return !parentId || !idSet.has(parentId);
        })
        .map(({ name }) => name);
      map.set(admin.id, topLevelNames.length ? topLevelNames.join(', ') : t('admin.roles.allScopes'));
    }
    return map;
  }, [list, ouParentById, t]);

  const target: AdminEditorTarget | null = editor.mode === 'closed' ? null : editor;

  return (
    <AdminSection title={t('admin.roles.title')} description={t('admin.roles.desc')}>
      <div className="admin-roles-panel">
      <div className="adm-roles-toolbar">
        <div className="adm-search">
          <i className="fas fa-magnifying-glass" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('admin.roles.searchEmail')} aria-label={t('admin.roles.searchEmail')} />
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setEditor({ mode: 'create' })}>
          <i className="fas fa-user-plus" /> {t('admin.roles.addAdmin')}
        </button>
      </div>

      <StateBlock
        isLoading={admins.isLoading}
        isError={admins.isError}
        error={admins.error}
        isEmpty={filtered.length === 0}
        emptyText={t('admin.roles.empty')}
        onRetry={() => { admins.refetch(); }}
      >
        <div className="adm-table-wrap">
          <table className="adm-table adm-table--glass">
            <thead>
              <tr>
                <th>{t('admin.roles.colAdmin')}</th>
                <th>{t('admin.roles.colRole')}</th>
                <th>{t('admin.roles.colScope')}</th>
                <th>{t('admin.roles.colStatus')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pg.slice.map((a: AdminUserDto, posicion: number) => {
                // El color por posición debe seguir la secuencia de la lista COMPLETA, no la de la
                // página visible: con el índice del `.map()` (siempre 0-based por página) la misma
                // persona cambiaba de color al paginar y la primera fila de cada página repetía el
                // color de la primera de la anterior.
                const posicionGlobal = (pg.page - 1) * pg.pageSize + posicion;
                return (
                  <tr key={a.id} className={target?.mode === 'edit' && target.admin.id === a.id ? 'adm-row--selected' : ''}>
                    <td>
                      <div className="adm-admin-cell">
                        <InitialsAvatar name={a.displayName ?? a.email} baseClass="rp-avatar" colorIndex={posicionGlobal} />
                        <div>
                          <div className="adm-admin-name">{a.displayName ?? a.email}</div>
                          <div className="adm-admin-email">{a.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className="adm-role-badge">{t(`admin.roles.role.${a.role}`)}</span></td>
                    <td>{scopeDisplayByAdminId.get(a.id)}</td>
                    <td>
                      <span className={`adm-status ${a.active ? 'adm-status--on' : 'adm-status--off'}`}>
                        {a.active ? t('admin.roles.active') : t('admin.roles.inactive')}
                      </span>
                    </td>
                    <td>
                      {a.role !== 'AdminGlobal' && canManageUser(activeRoles, [a.role]) && (
                        <div className="adm-roles-row-actions">
                          <button type="button" className="btn btn--link" onClick={() => setEditor({ mode: 'edit', admin: a })}>
                            <i className="fas fa-sliders" /> {t('admin.roles.edit')}
                          </button>
                          <button type="button" className="btn btn--link" onClick={() => setRemoveTarget(a)}>
                            <i className="fas fa-user-minus" /> {t('admin.roles.remove')}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <AdminPagerFooter total={filtered.length} page={pg.page} pageSize={pg.pageSize} onPage={pg.setPage} onPageSize={pg.setPageSize} />
      </StateBlock>
      </div>

      <AdminRoleEditorModal
        target={target}
        existingAdmins={allAdmins}
        ouItems={scopedOuItems}
        activeRoles={activeRoles}
        onClose={() => setEditor({ mode: 'closed' })}
      />

      {removeTarget && (
        <ConfirmModal
          type="danger"
          title={t('admin.roles.removeConfirmTitle')}
          message={t('admin.roles.removeConfirmMsg', { name: removeTarget.displayName ?? removeTarget.email })}
          confirmText={t('admin.roles.removeConfirm')}
          onConfirm={confirmRemove}
          onClose={() => setRemoveTarget(null)}
        />
      )}
      <Toast toast={toast} />
    </AdminSection>
  );
}

export default AdminRoles;
